import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {ArtisticObjectListing} from '../../../admin/artisticobjects/types/ArtisticObject';
import {compareLocationCodes, formatLocation} from '../../utils/locationParser';
import {getThumbnailUrl} from '../../utils/imageUtils';

interface DetailsArtisticObjectsListProps {
  artisticObjects: ArtisticObjectListing[];
  onArtisticObjectClick?: (artisticObject: ArtisticObjectListing) => void;
  title?: string;
  getLink?: (artisticObject: ArtisticObjectListing) => string | { pathname: string; search?: string };
}

const DetailsArtisticObjectsList: React.FC<DetailsArtisticObjectsListProps> = ({
  artisticObjects,
  onArtisticObjectClick,
                                                                                 title,
                                                                                 getLink
}) => {
  const {t} = useTranslation();
  const displayTitle = title || t('details.sections.artisticObjects');

  if (artisticObjects.length === 0) {
    return null;
  }

  // Sort artistic objects: first those without parent, then by location code
  const sortedObjects = [...artisticObjects].sort((a, b) => {
    // Objects without parent come first
    if (!a.parent && b.parent) return -1;
    if (a.parent && !b.parent) return 1;

    // If both have parents or both don't have parents, sort by location code
    return compareLocationCodes(a.locationCode, b.locationCode);
  });

  const handleObjectClick = (artisticObject: ArtisticObjectListing, e: React.MouseEvent) => {
    if (onArtisticObjectClick) {
      // If it's a normal click (not right-click/cmd-click), handle it via callback
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button !== 1) {
        e.preventDefault();
        onArtisticObjectClick(artisticObject);
      }
    }
  };

  return (
    <div id="artistic-objects-card" className="bg-white shadow-sm p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">{displayTitle}</h3>
      <div className="space-y-3">
        {sortedObjects.map((artisticObject) => {
          const content = (
            <>
              {/* Image */}
              <div className="flex-shrink-0 w-16 h-16 bg-stone-100 rounded overflow-hidden">
                {artisticObject.firstImage ? (
                  <img
                    className="w-full h-full object-cover"
                    src={getThumbnailUrl(artisticObject.firstImage)}
                    alt={artisticObject.firstImage.originalName || artisticObject.name}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400">
                    <i className="fa fa-image text-xl"></i>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-grow min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-stone-900 truncate">
                    {artisticObject.name}
                  </h4>
                </div>

                {formatLocation(artisticObject.locationCode, artisticObject.locationDescription) && (
                  <div className="text-sm text-stone-600 mt-1">
                    <span
                      className="font-medium">{t('details.labels.location')}:</span> {formatLocation(artisticObject.locationCode, artisticObject.locationDescription)}
                  </div>
                )}
              </div>

              {/* Arrow icon for clickable items */}
              {(onArtisticObjectClick || getLink) && (
                <div className="flex-shrink-0 text-stone-400">
                  <i className="fa fa-chevron-right"></i>
                </div>
              )}
            </>
          );

          if (getLink) {
            return (
              <Link
                key={artisticObject.id}
                to={getLink(artisticObject)}
                className="flex items-center gap-4 p-3 rounded-sm border border-stone-200 cursor-pointer hover:bg-stone-50 hover:border-stone-300"
                onClick={(e) => handleObjectClick(artisticObject, e)}
              >
                {content}
              </Link>
            );
          }

          return (
            <div
              key={artisticObject.id}
              className={`flex items-center gap-4 p-3 rounded-sm border border-stone-200 ${onArtisticObjectClick ? 'cursor-pointer hover:bg-stone-50 hover:border-stone-300' : ''
              }`}
              onClick={(e) => handleObjectClick(artisticObject, e)}
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DetailsArtisticObjectsList;
